import React, { useEffect } from 'react';
import { ArrowRight, History, Moon, X } from 'lucide-react';
import { PunchHistoryItem } from '../../interfaces/TimesheetEditInterface';
import { fmtWhen, srcClass } from './changeLogFormat';
import {
  entityKindLabel,
  fieldLabel,
  fmtChangeValue,
  fmtPunchClock,
  isImportedEvent,
  punchDay,
  punchEventWho,
  timesChanged,
  visibleChanges,
} from './punchHistory';
import './HistoryPanel.css';
import './PunchHistoryModal.css';

/**
 * TAS-365 — "click an edited punch": the full change history of one punch (or of
 * a day's removed punches) from GET /admin/timesheetEdit/punchHistory.
 *
 * Shell reuses the review console's modal classes (.etrc-overlay / .etrc-mw-*,
 * styled by EmployeeTimesheetReviewConsole.css, the only host); source badges and
 * action chips reuse the HistoryPanel's .pe-src / .pe-actx. Esc or the scrim closes.
 */

export interface PunchHistoryModalProps {
  /** One punch, or every removed punch of a day. */
  items: PunchHistoryItem[];
  title: string;
  /** e.g. "Mon, Oct 5 · Rosegate – Ph. 2". */
  subtitle: string;
  /** A punch-level note above the history — the overnight on-call split
   *  ("Overnight shift — continues on the next day (12:00 AM split)"). Fixed for the
   *  modal's lifetime, so the content never changes size while open. */
  note?: string | null;
  employeeName: string;
  onClose: () => void;
}

const ACTION_CLASS: Record<string, string> = { Added: 'ins', Edited: 'upd', Deleted: 'del' };

const PunchHistoryBody: React.FC<{ item: PunchHistoryItem; employeeName: string; showKind: boolean }> = ({
  item, employeeName, showKind,
}) => {
  const day = punchDay(item);
  const moved = timesChanged(item.original, item.current);
  const o = item.original;
  const c = item.current;

  const side = (label: string, orig: string | null, origOff: string | null, cur: string | null | undefined, curOff: string | null | undefined, changed: boolean) => (
    <div className="ph-side">
      <span className="ph-lbl">{label}</span>
      {changed ? (
        <>
          <span className="ph-was">{fmtPunchClock(orig, origOff, day)}</span>
          <ArrowRight className="ph-ar" />
          <span className="ph-now">{fmtPunchClock(cur ?? null, curOff, day)}</span>
        </>
      ) : (
        <span className={item.deleted ? 'ph-was' : 'ph-same'}>
          {fmtPunchClock(item.deleted || !c ? orig : cur ?? orig, item.deleted || !c ? origOff : curOff ?? origOff, day)}
        </span>
      )}
    </div>
  );

  return (
    <section className="ph-punch">
      {showKind && (
        <div className="ph-kind">{entityKindLabel(item.entityType)} #{item.entityID}</div>
      )}
      <div className={`ph-orig${item.deleted ? ' gone' : ''}`}>
        <span className="ph-orig-h">{item.deleted ? 'Removed punch' : 'Original → now'}</span>
        {side('In', o.in, o.inOffset, c?.in, c?.inOffset, moved.in)}
        {side('Out', o.out, o.outOffset, c?.out, c?.outOffset, moved.out)}
      </div>

      {!item.complete && (
        <p className="ph-note">
          {item.origin === 'auditFallback'
            ? 'Read from the old audit log — earlier changes may be missing.'
            : 'This punch predates the change log; its earlier history was not recorded.'}
        </p>
      )}

      <ol className="ph-timeline">
        {item.events.map((e, i) => {
          const changes = visibleChanges(e);
          return (
            <li key={`${e.at}-${i}`} className={`ph-ev ${ACTION_CLASS[e.action] ?? 'upd'}`}>
              <div className="ph-evhead">
                <span className={`pe-actx ${ACTION_CLASS[e.action] ?? 'upd'}`}>{e.action === 'Edited' ? 'Changed' : e.action}</span>
                <span className="ph-who">{punchEventWho(e, employeeName)}</span>
                <span className={`pe-src ${srcClass(e.source)}`}>{e.source || '?'}</span>
              </div>
              <div className="ph-when">
                {fmtWhen(e.at)}
                {isImportedEvent(item, e) && <span className="ph-imported">Imported from audit log</span>}
              </div>
              {changes.length > 0 && (
                <dl className="ph-changes">
                  {changes.map((ch, ci) => (
                    <React.Fragment key={`${ch.field}-${ci}`}>
                      <dt>{fieldLabel(ch.field)}</dt>
                      <dd>
                        {e.action === 'Added' ? (
                          <span className="new">{fmtChangeValue(ch.field, ch.new, ch.newOffset, day)}</span>
                        ) : e.action === 'Deleted' ? (
                          <span className="old">{fmtChangeValue(ch.field, ch.old, ch.oldOffset, day)}</span>
                        ) : (
                          <>
                            <span className="old">{fmtChangeValue(ch.field, ch.old, ch.oldOffset, day)}</span>
                            <ArrowRight className="ph-ar" />
                            <span className="new">{fmtChangeValue(ch.field, ch.new, ch.newOffset, day)}</span>
                          </>
                        )}
                      </dd>
                    </React.Fragment>
                  ))}
                </dl>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
};

const PunchHistoryModal: React.FC<PunchHistoryModalProps> = ({ items, title, subtitle, note, employeeName, onClose }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="etrc-overlay" onClick={onClose}>
      <div
        className="etrc-mw-modal ph-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="etrc-mw-header ph-head">
          <div className="etrc-mw-icon-wrap ph-icon">
            <History size={20} />
          </div>
          <div>
            <div className="etrc-mw-title">{title}</div>
            <div className="etrc-mw-subtitle">{subtitle}</div>
          </div>
          <button type="button" className="etrc-mw-close" onClick={onClose} title="Close">
            <X size={18} />
          </button>
        </div>
        <div className="etrc-mw-body ph-body">
          {note && (
            <p className="ph-note ph-note-top">
              <Moon size={13} aria-hidden="true" />
              {note}
            </p>
          )}
          {items.length === 0 && <p className="ph-note">No change history recorded for this punch.</p>}
          {items.map((item) => (
            <PunchHistoryBody
              key={`${item.entityType}:${item.entityID}`}
              item={item}
              employeeName={employeeName}
              showKind={items.length > 1}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default PunchHistoryModal;
